import React from 'react'
import { MapSystem } from 'roadie-ui'
import styled from 'styled-components'
import media from 'styles/mixins/media'
import start from 'components/map/markers/start.svg?url'
import stopDefault from 'components/map/markers/stop.svg?url'
import stopReturn from 'components/map/markers/stop-return.svg?url'
import { Consolidation } from './BlockPayments/TransactionCell'
import { ConsolidationDetails } from 'types/consolidationDetail/consolidation'
import { GigDetails } from 'types/gigDetails'

export type Location = {
  latitude: number
  longitude: number
  city: string
  state: string
  zip: string
  friendly_name: string
}

export type Stop = {
  index: number
  gig_ids: number[]
  category: string
  last_event: string | null
  signature_required: boolean
  estimated_stop_time: number
  location: Location
  support_ids: string[]
  automated_return_gig: boolean
  automated_pickup_gig: boolean
  eta: string | null
}

export type Marker = {
  longitude: number
  latitude: number
  iconUrl: string
  size: number
}

const MapContainer = styled.div`
  width: calc(100% + 8rem);
  margin-left: -4rem;
  margin-top: -2.4rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.greyLight};

  ${media.mobile`
    width: calc(100% + 4.8rem);
    margin-left: -2.4rem;
  `}
`

const Mileage = styled.div`
  position: absolute;
  right: 1.4rem;
  bottom: 1.6rem;
  width: 8.8rem;
  height: 3rem;
  border-radius: 1.6rem;
  color: ${(props) => props.theme.colors.primaryBase};
  background-color: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.greyLight};
  font-size: 1.3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  letter-spacing: 0.04rem;
  font-weight: 500;
`

export function getMarkersFromConsolidations(consolidations: Consolidation[]): Marker[] {
  const seen: Array<{ latitude: number; longitude: number }> = []

  const hasSeen = (location: { latitude: number; longitude: number }) => {
    return !!seen.find((loc) => {
      return loc.latitude === location.latitude && loc.longitude === location.longitude
    })
  }

  const markers: Marker[] = []

  for (const consolidation of consolidations) {
    consolidation.stops.forEach((stop) => {
      if (hasSeen(stop.location)) {
        return
      } else {
        seen.push(stop.location)
      }

      const isPickup = stop.category === 'pickup'
      const isStop = !isPickup
      const size = isStop ? 35 : 55
      const iconUrl = isPickup ? start : stopDefault

      markers.push({
        longitude: stop.location.longitude,
        latitude: stop.location.latitude,
        iconUrl,
        size,
      })
    })
  }

  return markers
}

// Drive.jsx-style getMarkers function (more complex logic with gig status)
export function getMarkersFromConsolidationAndGigs(consolidation: ConsolidationDetails, gigs: GigDetails[]): Marker[] {
  const seen: Array<{ latitude: number; longitude: number }> = []

  const hasSeen = (location: { latitude: number; longitude: number }) => {
    return !!seen.find((loc) => {
      return loc.latitude === location.latitude && loc.longitude === location.longitude
    })
  }

  const markers: Marker[] = []

  consolidation.stops.forEach((stop) => {
    if (hasSeen(stop.location)) {
      return
    } else {
      seen.push(stop.location)
    }

    const gig = gigs.find((g) => g.id === stop.gigIds[0])
    const isPickup = stop.category === 'pickup'
    const isStop = !isPickup
    const size = isStop ? 35 : 55
    const isReturn = gig?.status === 'delivery_attempted'

    let iconUrl
    if (isPickup && isReturn) {
      iconUrl = stopDefault
    } else if (isStop && isReturn) {
      iconUrl = stopReturn
    } else if (isPickup) {
      iconUrl = start
    } else {
      iconUrl = stopDefault
    }

    markers.push({
      longitude: stop.location.longitude,
      latitude: stop.location.latitude,
      iconUrl,
      size,
    })
  })

  return markers
}

interface MapDisplayProps {
  // For BlockDetails usage (multiple consolidations)
  consolidations?: Consolidation[]

  // For Drive.jsx usage (single consolidation + gigs)
  consolidation?: ConsolidationDetails
  gigs?: GigDetails[]

  // Common props
  height?: string
  showMileage?: boolean
  showRoute?: boolean
  route?: string
  mileageText?: string
  isPOTO?: boolean
}

export const MapDisplay: React.FC<MapDisplayProps> = ({
  consolidations,
  consolidation,
  gigs,
  height = '30rem',
  showMileage = true,
  showRoute = false,
  route,
  mileageText,
  isPOTO = false,
}) => {
  let markers: Marker[] = []
  let totalDistance = 0
  let routeToShow = route

  if (consolidations) {
    // BlockDetails usage - multiple consolidations
    markers = getMarkersFromConsolidations(consolidations)
    totalDistance = consolidations.reduce((sum, c) => sum + (c.estimated_distance || 0), 0)
  } else if (consolidation && gigs) {
    // Drive.jsx usage - single consolidation with gigs
    markers = getMarkersFromConsolidationAndGigs(consolidation, gigs)
    totalDistance = consolidation.estimatedDistance || 0
    routeToShow = routeToShow || consolidation.route
  }

  const displayMileage = mileageText || `${totalDistance.toFixed(1)} miles`

  return (
    <MapContainer>
      <MapSystem.Map height={height} width="100%">
        {showRoute && routeToShow && !isPOTO && (
          <MapSystem.Route fitInMap route={routeToShow} color="text-primary-base" />
        )}
        {markers.map(({ longitude, latitude, iconUrl, size }) => (
          <MapSystem.Marker
            key={[longitude, latitude].join(':')}
            longitude={longitude}
            latitude={latitude}
            size={size}
            imageUrl={iconUrl}
            fitInMap
          />
        ))}
        {showMileage && !isPOTO && <Mileage>{displayMileage}</Mileage>}
      </MapSystem.Map>
    </MapContainer>
  )
}
